// Task item row component for execution interface - Mobile-optimized
// CREATED: Individual row for each task item with checkbox and optional text input
// UPDATED: Removed checkbox - now display-only with text input support
// UPDATED: Removed photo upload functionality
// UPDATED: Simplified text input display, removed placeholder and labels
// UPDATED: Added read-only mode for completed tasks
// UPDATED: Removed red border, added discrete "Obligatoire" label, disabled focus on completed tasks
// UPDATED: Removed auto-save on blur, now using local state only with callback to parent
// UPDATED: Added isReadOnly prop for temporal access control
// UPDATED: Removed onToggleComplete prop (individual item completion removed)
// UPDATED: KPI system v1 - numeric items use type="number" with inputMode="numeric" + inline validation

import { useState, useEffect } from 'react';
import { AssignedTaskItem } from '../../lib/execution';

interface TaskItemRowProps {
  item: AssignedTaskItem;
  onTextChange: (itemId: string, value: string) => void;
  isTaskCompleted?: boolean;
  isReadOnly?: boolean;
}

export function TaskItemRow({ item, onTextChange, isTaskCompleted = false, isReadOnly = false }: TaskItemRowProps) {
  const [textValue, setTextValue] = useState(item.text_value || '');
  const [numericError, setNumericError] = useState(false);

  useEffect(() => {
    setTextValue(item.text_value || '');
  }, [item.text_value]);

  const isNumeric = item.text_input_type === 'number';

  const handleTextChange = (value: string) => {
    setTextValue(value);
    if (isNumeric && value.trim() !== '') {
      setNumericError(isNaN(parseFloat(value.trim())));
    } else {
      setNumericError(false);
    }
    onTextChange(item.id, value);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
      <div>
        <h4 className="font-medium text-gray-900 text-base mb-1">
          {item.title}
        </h4>

        {item.description && (
          <p className="text-sm text-gray-600 mt-1">
            {item.description}
          </p>
        )}

        {item.requires_text_input && (
          <div className="mt-3">
            {item.is_text_input_required && !isTaskCompleted && (
              <p className="text-xs text-gray-500 mb-2">
                Obligatoire
              </p>
            )}
            <input
              type={isNumeric ? 'number' : 'text'}
              inputMode={isNumeric ? 'decimal' : undefined}
              value={textValue}
              onChange={(e) => handleTextChange(e.target.value)}
              readOnly={isTaskCompleted || isReadOnly}
              placeholder={item.text_input_placeholder || (isNumeric ? '0' : '')}
              className={`w-full px-4 py-3 border rounded-lg transition-all text-base ${
                isTaskCompleted || isReadOnly
                  ? 'bg-gray-50 text-gray-700 border-gray-200 cursor-default focus:outline-none'
                  : numericError
                    ? 'border-red-400 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400'
                    : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500'
              }`}
            />
            {numericError && (
              <p className="text-xs text-red-500 mt-1">Nombre attendu</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
